import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import RedirectLinks from './RedirectLinks';
import { useAuth } from '../context/AuthContext';
import './PolicySubmit.css';

const VALID_PO = /^PO\d{8}$/i;

const POLICY_TYPE_OPTIONS = [
  { value: 'MOTOR_INSURANCE', label: 'MOTOR_INSURANCE (ภาคสมัครใจ)' },
  { value: 'COMPULSORY_MOTOR_INSURANCE', label: 'COMPULSORY_MOTOR_INSURANCE (พรบ.)' },
  { value: 'MOTOR_ADD_ON', label: 'MOTOR_ADD_ON' },
  { value: 'MICROPLUS', label: 'MICROPLUS' },
  { value: 'PET', label: 'PET' },
  { value: 'USE_CAR_WARRANTY', label: 'USE_CAR_WARRANTY' }
];

// Pull the APFS payment redirect URL out of the submit response, tolerating
// the different shapes the backend may return it in.
function getRedirectUrl(result) {
  if (!result) return null;
  return (
    result.data?.redirectUrl ||
    result.raw?.data?.redirectUrl ||
    result.raw?.redirectUrl ||
    result.redirectUrl ||
    null
  );
}

export default function PolicySubmit({ initialPO, onInitialPOConsumed }) {
  const { can } = useAuth();

  const [poNumber, setPoNumber] = useState('');
  const [provider, setProvider] = useState('APFS');
  const [action, setAction] = useState('create'); // 'create' (L.2) | 'onboarding' (L.7)

  // Edit rights differ by action: create=L.2 (policy-submit), onboarding=L.7 (apfs-submitted)
  const canSubmit = can(action === 'create' ? 'policy-submit' : 'apfs-submitted', 'edit');
  const [crm, setCrm] = useState(null);
  const [preview, setPreview] = useState(null);

  // L.2 Create — full editable APFS payload (deep-mutable)
  const [apfsPayload, setApfsPayload] = useState(null);

  const [decision, setDecision] = useState('APPROVED');
  const [insurerCode, setInsurerCode] = useState('');
  const [policyType, setPolicyType] = useState('MOTOR_INSURANCE');
  const [policyNumber, setPolicyNumber] = useState('');
  const [policyUrl, setPolicyUrl] = useState('');

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // Duplicate-submission guard for L.2 Create.
  // Shape: { winning, attempts, counts } — winning != null means BLOCK.
  const [dupCheck, setDupCheck] = useState(null);

  const copyToClipboard = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
      })
      .catch(() => window.prompt('คัดลอกข้อความนี้:', text));
  };

  // APFS programs catalog (for the Program dropdown)
  const [programs, setPrograms] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/mappings/programs?active=true`)
      .then((r) => { if (r.data?.success) setPrograms(r.data.data || []); })
      .catch(() => {});
  }, []);

  // Auto-lookup when initialPO is passed from URL param (?apfs_po=...)
  useEffect(() => {
    if (!initialPO) return;
    setPoNumber(initialPO);
    if (onInitialPOConsumed) onInitialPOConsumed();
    // Trigger lookup after state settles
    setTimeout(() => {
      handleLookupByPO(initialPO);
    }, 0);
  }, [initialPO]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setCrm(null);
    setPreview(null);
    setApfsPayload(null);
    setInsurerCode('');
    setPolicyType('MOTOR_INSURANCE');
    setPolicyNumber('');
    setPolicyUrl('');
    setError('');
    setResult(null);
    setDupCheck(null);
  };

  // Program is the only L.2 field the operator may change; everything else
  // comes from the PO (and the backend re-pulls it on submit anyway).
  const setField = (key, value) => {
    setApfsPayload((p) => ({ ...p, [key]: value }));
  };

  const handleLookupByPO = async (po) => {
    setError('');
    setResult(null);
    const trimmed = (po || '').trim().toUpperCase();
    if (!VALID_PO.test(trimmed)) {
      setError('PO format invalid (expected: PO + 8 digits)');
      return;
    }

    setLoading(true);
    setDupCheck(null);
    try {
      const lookup = await axios.post(`${API_BASE_URL}/api/po/lookup`, {
        po_number: trimmed,
        skipEnrichment: true
      });
      if (!lookup.data?.success) throw new Error(lookup.data?.error || 'PO not found');

      const original = lookup.data.processedRows?.[0]?.originalData;
      if (!original) throw new Error('CRM payload missing in lookup response');
      setCrm(original);

      // Fire-and-forget duplicate check so the banner can show before the
      // user even reads the form. Never blocks lookup — if it fails the
      // backend submit guard still catches anything important.
      if (original.PO_ID) {
        axios
          .get(`${API_BASE_URL}/api/policy/dup-check`, { params: { propId: original.PO_ID } })
          .then((r) => {
            if (r.data?.success) setDupCheck(r.data);
          })
          .catch(() => { /* silent — backend will re-check on submit */ });
      }

      const previewResp = await axios.post(`${API_BASE_URL}/api/policy/preview`, {
        crm: original,
        provider
      });
      if (previewResp.data?.success) {
        setPreview(previewResp.data);
        if (previewResp.data.resolved?.insurerCode) {
          setInsurerCode(previewResp.data.resolved.insurerCode);
        }
        if (previewResp.data.resolved?.apfsPolicyType) {
          setPolicyType(previewResp.data.resolved.apfsPolicyType);
        }
        setPolicyNumber(original.Policy_Number || '');
        if (previewResp.data.apfsCreatePayload) {
          setApfsPayload(previewResp.data.apfsCreatePayload);
        }
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = () => handleLookupByPO(poNumber);

  const handleSubmit = async () => {
    setError('');
    setResult(null);
    if (provider !== 'APFS') {
      setError('AME submission must use the existing PO Lookup → Submit flow.');
      return;
    }
    if (!crm) {
      setError('Please lookup PO first.');
      return;
    }

    // Action-specific validation
    if (action === 'onboarding') {
      if (!insurerCode) {
        setError('Insurer code is required (add via Admin Mappings if missing).');
        return;
      }
      if (decision === 'APPROVED' && !policyNumber.trim()) {
        setError('Policy Number is required when APPROVED.');
        return;
      }
    }

    // No client-side hard block anymore: the backend cross-checks the real
    // state at APFS (L.11 LMS) and decides whether to block, confirm, or allow.
    setSubmitting(true);
    const send = async (confirmResend) => {
      const body = {
        crm,
        provider,
        action
      };

      if (action === 'create') {
        // Send the user-edited payload directly. Backend will sign and
        // forward without re-applying CRM defaults.
        if (apfsPayload && apfsPayload.propId) {
          body.apfsPayload = apfsPayload;
        }
        if (confirmResend) body.confirmResend = true;
      } else if (action === 'onboarding') {
        body.decision = decision;
        body.insurerCode = insurerCode;
        body.policyNumber = policyNumber.trim();
        body.policyUrl = policyUrl.trim() || undefined;
        body.policyType = policyType;
      }

      return axios.post(`${API_BASE_URL}/api/policy/submit`, body);
    };

    try {
      const resp = await send(false);
      setResult(resp.data);
      if (!resp.data?.success) {
        setError(resp.data?.error || resp.data?.message || 'Submit failed');
      } else if (action === 'create') {
        // Refresh dup-check so the banner now reflects the new sent row.
        setDupCheck((d) => ({ ...(d || { attempts: [], counts: {} }), winning: { status: 'sent' } }));
      }
    } catch (e) {
      const data = e.response?.data || null;
      const code = data?.code;

      if (code === 'DUPLICATE_PROP_ID') {
        // A live contract exists at APFS (verified via L.11). Block; an admin
        // must Reject it on the APFS Submitted page to allow a resubmit.
        setDupCheck({ winning: data.existing, attempts: data.attempts || [], counts: data.counts || {} });
        setError(data.error || `Prop ID ${crm.PO_ID} has a live contract at APFS. Re-submission is blocked.`);
        setResult(null);
      } else if (code === 'CONFIRM_RESEND' || code === 'DUPLICATE_UNVERIFIED') {
        // Either prior attempts with none successful, OR a local "successful"
        // row that APFS LMS doesn't confirm (not found / unverifiable).
        setDupCheck({ winning: null, attempts: data.attempts || [], counts: data.counts || {} });
        const ok = window.confirm(
          `${data.error || 'Confirm resubmit?'}\n\nResend ${crm.PO_ID} to APFS now?`
        );
        if (ok) {
          try {
            const resp2 = await send(true);
            setResult(resp2.data);
            if (!resp2.data?.success) {
              setError(resp2.data?.error || resp2.data?.message || 'Submit failed');
            }
          } catch (e2) {
            setError(e2.response?.data?.error || e2.message);
            setResult(e2.response?.data || null);
          }
        } else {
          setError(''); // user cancelled
        }
      } else {
        setError(e.response?.data?.error || e.message);
        setResult(e.response?.data || null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="policy-submit">
      <div className="ps-header">
        <h2>APFS Submit</h2>
        <p className="ps-sub">
          Submit cases to APFS finance. AME pipeline is unaffected.
        </p>
      </div>

      <div className="ps-card">
        <div className="ps-row">
          <label>Action</label>
          <div className="ps-action-tabs">
            <button
              type="button"
              className={`ps-action-tab ${action === 'create' ? 'active' : ''}`}
              onClick={() => { setAction('create'); setResult(null); setError(''); }}
            >
              <strong>🆕 Create Contract (L.2)</strong>
              <small>Initial submit — before insurer issues policy</small>
            </button>
            <button
              type="button"
              className={`ps-action-tab ${action === 'onboarding' ? 'active' : ''}`}
              onClick={() => { setAction('onboarding'); setResult(null); setError(''); }}
            >
              <strong>✅ Submit Policy Number (L.7)</strong>
              <small>After insurer issues — APPROVED / REJECTED</small>
            </button>
          </div>
        </div>

        <div className="ps-row">
          <label>Provider</label>
          <div className="ps-radio-group">
            <label className="ps-radio">
              <input
                type="radio"
                name="provider"
                value="APFS"
                checked={provider === 'APFS'}
                onChange={() => { setProvider('APFS'); reset(); }}
              />
              APFS
            </label>
            <label className="ps-radio">
              <input
                type="radio"
                name="provider"
                value="AME"
                checked={provider === 'AME'}
                onChange={() => { setProvider('AME'); reset(); }}
                disabled
                title="AME submission uses existing /api/insurance/* — not handled here"
              />
              AME (use existing PO Lookup tab)
            </label>
          </div>
        </div>

        <div className="ps-row">
          <label>PO Number</label>
          <div className="ps-inline">
            <input
              type="text"
              placeholder="PO69003115"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
              disabled={loading || submitting}
            />
            <button
              type="button"
              onClick={handleLookup}
              disabled={loading || submitting}
              className="ps-btn ps-btn-primary"
            >
              {loading ? 'Looking up…' : 'Lookup'}
            </button>
            {(crm || preview) && (
              <button
                type="button"
                onClick={() => { setPoNumber(''); reset(); }}
                disabled={loading || submitting}
                className="ps-btn ps-btn-ghost"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {error && <div className="ps-error">⚠️ {error}</div>}

      {preview?.warnings?.length > 0 && (
        <div className="ps-warnings">
          {preview.warnings.map((w) => (
            <div className="ps-warning" key={w.code}>⚠️ {w.message}</div>
          ))}
        </div>
      )}

      {crm && (
        <div className="ps-card">
          <h3>CRM Snapshot</h3>
          <div className="ps-grid">
            <div><span className="k">Customer:</span> {crm.Customer_Title} {crm.Customer_FName} {crm.Customer_LName}</div>
            <div><span className="k">Phone:</span> {crm.Customer_Phone}</div>
            <div><span className="k">Citizen ID:</span> {crm.Customer_IDcard}</div>
            <div><span className="k">Car:</span> {crm.Car_Brand_Name} {crm.Car_Model_Name} ({crm.Car_Sub_Model})</div>
            <div><span className="k">Plate:</span> {crm.Car_Plate_No} {crm.Car_Plate_Provice}</div>
            <div><span className="k">VIN:</span> {crm.Frame_No}</div>
            <div><span className="k">Insurance:</span> {crm.Insurance_Company}</div>
            <div><span className="k">Type:</span> {crm.insurance_Type}</div>
            <div><span className="k">Coverage:</span> {crm.Order_Coverage_Start_Date} → {crm.Order_Coverage_End_Date}</div>
            <div><span className="k">Premium:</span> {crm.Order_Total_Premium}</div>
          </div>

          <details className="ps-raw">
            <summary>ดู payload ต้นฉบับจาก iClickCRM (raw JSON)</summary>
            <div className="ps-raw-actions">
              <button
                type="button"
                className="ps-payload-copy"
                onClick={() => copyToClipboard(JSON.stringify(crm, null, 2), 'payload')}
              >
                {copiedKey === 'payload' ? '✅ คัดลอกแล้ว' : '📋 คัดลอก payload'}
              </button>
            </div>
            <pre>{JSON.stringify(crm, null, 2)}</pre>
          </details>
        </div>
      )}

      {crm && action === 'create' && apfsPayload && (
        <div className="ps-card">
          <h3>Create Contract (L.2)</h3>
          <p className="ps-sub-inline">
            ข้อมูลทุกช่องดึงจาก PO ใน iClickCRM และแก้ไม่ได้ — เลือกได้เฉพาะ <b>Program</b>.
            ถ้าข้อมูลผิด ให้แก้ที่ iClickCRM แล้วกด Lookup ใหม่
            (ตอน Submit ระบบจะดึง PO ใหม่อีกครั้งเพื่อสร้าง payload).
            APFS จะตอบ <code>redirectURL</code> ให้ลูกค้าจ่ายมัดจำ
          </p>

          {/* ── Customer ── */}
          <h4 className="ps-section-h">👤 Customer</h4>
          <div className="ps-grid-form">
            <label>propId (PO)
              <input value={apfsPayload.propId || ''} readOnly />
            </label>
            <label>First Name <span className="req">*</span>
              <input value={apfsPayload.pHolderFirstName || ''} readOnly />
            </label>
            <label>Last Name <span className="req">*</span>
              <input value={apfsPayload.pHolderLastName || ''} readOnly />
            </label>
            <label>Phone <span className="req">*</span>
              <input value={apfsPayload.pHolderPhone || ''} readOnly />
            </label>
            <label>Email <span className="req">*</span>
              <input value={apfsPayload.pHolderEmail || ''} readOnly />
            </label>
            <label>Citizen / Passport ID <span className="req">*</span>
              <input value={apfsPayload.pHolderId || ''} readOnly />
            </label>
          </div>

          {/* ── Program / Payment ── */}
          <h4 className="ps-section-h">💳 Program & Payment</h4>
          <div className="ps-grid-form">
            <label>Program <span className="req">*</span>
              <select value={apfsPayload.program || ''} onChange={(e) => setField('program', e.target.value)}>
                <option value="" disabled>— Select program —</option>
                {programs.map((p) => {
                  const min = p.min_premium != null ? Number(p.min_premium).toLocaleString('th-TH') : null;
                  const max = p.max_premium != null ? Number(p.max_premium).toLocaleString('th-TH') : null;
                  const range = min ? (max ? ` · ${min}–${max}` : ` · ≥${min}`) : '';
                  const fee = p.fee_pct != null ? ` · ${p.fee_pct}%` : '';
                  return (
                    <option key={p.code} value={p.code}>
                      {p.code} ({p.installment_count} งวด{range}{fee})
                    </option>
                  );
                })}
                {/* Show currently-selected program even if it isn't in the active catalog */}
                {apfsPayload.program && !programs.some((p) => p.code === apfsPayload.program) && (
                  <option value={apfsPayload.program}>{apfsPayload.program} (not in catalog)</option>
                )}
              </select>
              {(() => {
                const sel = programs.find((p) => p.code === apfsPayload.program);
                if (!sel) return null;
                // Same rule as the server (installmentCount): the Installment[]
                // schedule wins; No_Of_Installment is often "0".
                const schedule = Array.isArray(crm?.Installment) ? crm.Installment.length : 0;
                const poCount = schedule || Number(crm?.No_Of_Installment) || 0;
                if (poCount && Number(sel.installment_count) && Number(sel.installment_count) !== poCount) {
                  return (
                    <small style={{ color: '#c62828' }}>
                      ⚠️ {sel.code} is {sel.installment_count} งวด but this PO has {poCount} งวด — submit will be blocked
                    </small>
                  );
                }
                const premium = Number(apfsPayload.transGrossPremium);
                if (Number.isFinite(premium) && sel.min_premium != null && premium < Number(sel.min_premium)) {
                  return (
                    <small style={{ color: '#c62828' }}>
                      ⚠️ Gross Premium {premium.toLocaleString('th-TH')} below minimum {Number(sel.min_premium).toLocaleString('th-TH')} for {sel.code}
                    </small>
                  );
                }
                return null;
              })()}
            </label>
            <label>Preferred Payment Method
              <input value={apfsPayload.preferredPaymentMethod || ''} readOnly />
            </label>
            <label>Agent ID
              <input value={apfsPayload.agentId || ''} readOnly />
            </label>
            <label>Channel Code
              <input value={apfsPayload.channelCode || ''} readOnly />
            </label>
          </div>

          {/* ── Premium ── */}
          <h4 className="ps-section-h">💰 Premium</h4>
          <div className="ps-grid-form">
            <label>Gross Premium <span className="req">*</span>
              <input type="number" step="0.01" value={apfsPayload.transGrossPremium ?? ''} readOnly />
            </label>
            <label>Discount
              <input type="number" step="0.01" value={apfsPayload.transGrossPremiumDiscount ?? ''} readOnly />
            </label>
            <label>Gross After Discount
              <input type="number" step="0.01" value={apfsPayload.transGrossPremiumAfterDiscount ?? ''} readOnly />
            </label>
            <label>Net Premium <span className="req">*</span>
              <input type="number" step="0.01" value={apfsPayload.transNetPremuim ?? ''} readOnly />
            </label>
            <label>VAT <span className="req">*</span>
              <input type="number" step="0.01" value={apfsPayload.vat ?? ''} readOnly />
            </label>
            <label>Stamp Duty <span className="req">*</span>
              <input type="number" step="0.01" value={apfsPayload.transStampDuty ?? ''} readOnly />
            </label>
          </div>

          {/* ── Coverage / Car ── */}
          <h4 className="ps-section-h">🚗 Coverage & Car</h4>
          <div className="ps-grid-form">
            <label>Start Date (dd/MM/yyyy)
              <input value={apfsPayload.startDate || ''} readOnly />
            </label>
            <label>End Date (dd/MM/yyyy)
              <input value={apfsPayload.endDate || ''} readOnly />
            </label>
            <label>Make (Brand)
              <input value={apfsPayload.makeC || ''} readOnly />
            </label>
            <label>Model
              <input value={apfsPayload.model || ''} readOnly />
            </label>
            <label>Year of Manufacture
              <input value={apfsPayload.yearOfManufacture || ''} readOnly />
            </label>
          </div>

          {/* ── Address ── */}
          <h4 className="ps-section-h">🏠 Address</h4>
          <div className="ps-grid-form">
            <label>Village / Building
              <input value={apfsPayload.address?.pHolderVillageBuilding || ''} readOnly />
            </label>
            <label>Soi / Road
              <input value={apfsPayload.address?.pHolderSoiRoad || ''} readOnly />
            </label>
            <label>Sub-District
              <input value={apfsPayload.address?.pHolderSubDistrict || ''} readOnly />
            </label>
            <label>District
              <input value={apfsPayload.address?.pHolderDistrict || ''} readOnly />
            </label>
            <label>Province
              <input value={apfsPayload.address?.pHolderProvince || ''} readOnly />
            </label>
            <label>Postal Code
              <input value={apfsPayload.address?.pHolderPostalCode || ''} readOnly />
            </label>
          </div>

          {/* ── Switch Back URLs ── */}
          <h4 className="ps-section-h">🔗 Redirect URLs (after payment)</h4>
          <div className="ps-grid-form">
            <label>switchBackURLSuccess
              <input value={apfsPayload.switchBackURLSuccess || ''} readOnly />
            </label>
            <label>switchBackURLFail
              <input value={apfsPayload.switchBackURLFail || ''} readOnly />
            </label>
          </div>

          {/* ── Policies (1st item only) ── */}
          {apfsPayload.policies?.length > 0 && (
            <>
              <h4 className="ps-section-h">📋 Policy</h4>
              <div className="ps-grid-form">
                <label>Policy Number
                  <input value={apfsPayload.policies[0].policyNumber || ''} readOnly />
                </label>
                <label>Policy Type
                  <input value={apfsPayload.policies[0].policyType || 'MOTOR_INSURANCE'} readOnly />
                </label>
                <label>Policy Gross Premium
                  <input type="number" step="0.01" value={apfsPayload.policies[0].transGrossPremium ?? ''} readOnly />
                </label>
                <label>Policy Net Premium
                  <input type="number" step="0.01" value={apfsPayload.policies[0].transNetPremuim ?? ''} readOnly />
                </label>
                <label>Policy VAT
                  <input type="number" step="0.01" value={apfsPayload.policies[0].vat ?? ''} readOnly />
                </label>
                <label>Policy Stamp Duty
                  <input type="number" step="0.01" value={apfsPayload.policies[0].transStampDuty ?? ''} readOnly />
                </label>
              </div>
            </>
          )}

          <details className="ps-payload">
            <summary>🔍 Final payload preview (สิ่งที่จะส่งไปจริง)</summary>
            <pre>{JSON.stringify(apfsPayload, null, 2)}</pre>
          </details>

          {dupCheck && (dupCheck.winning || (dupCheck.attempts && dupCheck.attempts.length > 0)) && (
            <div className="ps-dup warn">
              <div className="ps-dup-title">
                {dupCheck.winning
                  ? `⚠️ Prop ID ${crm.PO_ID} marked submitted locally (status: ${dupCheck.winning.status}). APFS LMS will be checked on submit — if no live contract, you can resubmit.`
                  : `⚠️ ${dupCheck.attempts.length} previous attempt(s) for ${crm.PO_ID} (none successful). You will be asked to confirm.`}
              </div>
              {dupCheck.counts && Object.keys(dupCheck.counts).length > 0 && (
                <div className="ps-dup-counts">
                  {Object.entries(dupCheck.counts).map(([s, n]) => (
                    <code key={s}>{s}: {n}</code>
                  ))}
                </div>
              )}
              {dupCheck.winning?.created_at && (
                <div>Submitted at: {new Date(dupCheck.winning.created_at).toLocaleString('th-TH', { hour12: false })}</div>
              )}
            </div>
          )}

          <div className="ps-actions">
            <button
              type="button"
              onClick={() => setApfsPayload(preview?.apfsCreatePayload || null)}
              disabled={submitting}
              className="ps-btn ps-btn-ghost"
            >
              ↺ Reset program
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              className="ps-btn ps-btn-primary"
              title={!canSubmit ? 'You do not have permission to submit' : (dupCheck?.winning ? 'Will verify against APFS LMS before resubmitting' : '')}
            >
              {submitting
                ? 'Submitting…'
                : !canSubmit
                  ? '🔒 No submit permission'
                  : dupCheck?.winning
                    ? '🔁 Verify & Resubmit to APFS'
                    : '🆕 Submit to APFS (Create Contract)'}
            </button>
          </div>
        </div>
      )}

      {crm && action === 'onboarding' && (
        <div className="ps-card">
          <h3>Submit Policy Number (L.7)</h3>

          <div className="ps-row">
            <label>Decision</label>
            <div className="ps-radio-group">
              <label className="ps-radio">
                <input
                  type="radio"
                  name="decision"
                  value="APPROVED"
                  checked={decision === 'APPROVED'}
                  onChange={() => setDecision('APPROVED')}
                />
                ✅ APPROVED
              </label>
              <label className="ps-radio">
                <input
                  type="radio"
                  name="decision"
                  value="REJECTED"
                  checked={decision === 'REJECTED'}
                  onChange={() => setDecision('REJECTED')}
                />
                ❌ REJECTED
              </label>
            </div>
          </div>

          <div className="ps-row">
            <label>Insurer Code (APFS)</label>
            <input
              type="text"
              value={insurerCode}
              onChange={(e) => setInsurerCode(e.target.value)}
              placeholder="e.g. TIP, TMSTH, AAGI"
            />
            {!preview?.resolved?.insurerCode && (
              <small className="ps-hint">
                ⚠️ No mapping found for "{crm.Insurance_Company}" — please enter manually or
                add via APFS Mappings.
              </small>
            )}
          </div>

          <div className="ps-row">
            <label>Policy Type</label>
            <select value={policyType} onChange={(e) => setPolicyType(e.target.value)}>
              {POLICY_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="ps-row">
            <label>Policy Number {decision === 'APPROVED' && <span className="req">*</span>}</label>
            <input
              type="text"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              placeholder="Policy number from insurer"
            />
          </div>

          <div className="ps-row">
            <label>Policy URL</label>
            <input
              type="text"
              value={policyUrl}
              onChange={(e) => setPolicyUrl(e.target.value)}
              placeholder="(optional)"
            />
          </div>

          <div className="ps-actions">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className={`ps-btn ${decision === 'APPROVED' ? 'ps-btn-primary' : 'ps-btn-danger'}`}
            >
              {submitting ? 'Submitting…' : `Submit ${decision} to APFS`}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className={`ps-result ${result.success ? 'ok' : 'err'}`}>
          <h4>{result.success ? '✅ Submitted' : '❌ Failed'}</h4>

          {getRedirectUrl(result) && (
            <div className="ps-redirect">
              <label>🔗 ลิงก์ชำระเงินสำหรับลูกค้า (redirectURL)</label>
              <RedirectLinks url={getRedirectUrl(result)} />
            </div>
          )}

          <pre>{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
